import { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { DeleteOutlined } from "@ant-design/icons";
import {
  RiShoppingCart2Fill,
  RiApps2Line,
  RiEmotionSadLine,
} from "react-icons/ri";
import Modal from "../Modal";
import IconPickerModal, { AVAILABLE_ICONS } from "../IconPickerModal";
import {
  ModalContent,
  LeftColumn,
  RightColumn,
  ModalTitle,
  CounterTag,
  SubTitle,
  CategoryTagsContainer,
  CategoryTag,
  ProductsList,
  ProductItem,
  ProductInfo,
  ProductName,
  ProductDescription,
  ProductActions,
  AddButton,
  EmptyState,
  EmptyProductsList,
  EmptyIcon,
  EmptyText,
  SelectedProductsList,
  SelectedProductItem,
  ProductLeftSide,
  ProductIcon,
  ProductRightSide,
  DeleteIcon,
  Footer,
  CancelButton,
  SaveButton,
} from "./styles";
import {
  saveHomepageProductsService,
  getHomepageProductsByEventService,
  updateHomepageProductsService,
  getProductsAndCategoriesByEventIdService,
} from "../../service/products.service";
import { useSelector } from "react-redux";
import { useToast } from "../../hooks/toast";

const MAX_PRODUCTS = 5;
const MIN_PRODUCTS = 3;
const DEFAULT_CATEGORY_KEY = "all_categories";

const ProductsModal = ({
  visible,
  onCancel,
  onSave,
  eventEditionCode,
  eventId,
}) => {
  const intl = useIntl();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(DEFAULT_CATEGORY_KEY);
  const [iconPickerVisible, setIconPickerVisible] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productIcons, setProductIcons] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const { token } = useSelector((state) => state.auth);
  const { addToast } = useToast();
  const isPt = intl.locale === "pt" || intl.locale === "pt-BR";

  const allCategoriesLabel = useMemo(
    () =>
      intl.formatMessage({ id: DEFAULT_CATEGORY_KEY, defaultMessage: "Todos" }),
    [intl],
  );

  const getIconByName = useCallback((iconName) => {
    const icon = AVAILABLE_ICONS.find((icon) => icon.name === iconName);
    return icon || { name: "RiApps2Line", component: RiApps2Line };
  }, []);

  const extractUniqueCategories = useCallback(
    (productsData) => {
      const categoriesSet = new Set();

      productsData.forEach((product) => {
        const categoryName = isPt
          ? product.categoryTitle
          : product.categoryEnglishTitle;

        if (categoryName) {
          categoriesSet.add(categoryName);
        }
      });

      return Array.from(categoriesSet).sort();
    },
    [isPt],
  );

  const getProducts = async () => {
    if (!eventId) {
      return;
    }

    try {
      const response = await getProductsAndCategoriesByEventIdService(
        eventId,
        token,
      );

      const productsData = response?.data?.productsForSale || [];

      const mappedProducts = productsData.map((product) => ({
        id: product.productId,
        name: isPt ? product.title : product.englishTitle,
        title: product.title,
        englishTitle: product.englishTitle,
        description: isPt ? product.description : product.englishDescription,
        category: isPt ? product.categoryTitle : product.categoryEnglishTitle,
        categoryTitle: product.categoryTitle,
        categoryEnglishTitle: product.categoryEnglishTitle,
      }));

      const uniqueCategories = extractUniqueCategories(productsData);
      setCategories(uniqueCategories);
      setProducts(mappedProducts);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "error_loading_products" }),
      });
      setProducts([]);
      setCategories([]);
    }
  };

  const loadHomepageProducts = async () => {
    if (!eventId) return;

    try {
      const response = await getHomepageProductsByEventService(eventId, token);
      const homepageProducts = response?.data?.products || [];

      if (homepageProducts.length > 0) {
        const mappedHomepageProducts = homepageProducts.map((hpProduct) => ({
          id: hpProduct.id,
          title: hpProduct.title || hpProduct.productLocalName,
          englishTitle: hpProduct.englishTitle || hpProduct.englishName,
          icon: hpProduct.icon,
          isSaved: true,
        }));

        setSelectedProducts(mappedHomepageProducts);
        setIsEditMode(true);

        const iconsMap = {};
        mappedHomepageProducts.forEach((product) => {
          if (product.icon) {
            iconsMap[product.id] = getIconByName(product.icon);
          }
        });
        setProductIcons(iconsMap);
      }
    } catch (error) {
      console.error("Erro ao carregar produtos da homepage:", error);
      if (error?.response?.status !== 404) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "error_loading_homepage_products",
          }),
        });
      }
    }
  };

  useEffect(() => {
    if (visible) {
      getProducts();
      loadHomepageProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, eventId]);

  const filteredProducts = useMemo(() => {
    if (activeCategory === DEFAULT_CATEGORY_KEY) {
      return products;
    }

    return products.filter((product) => product.category === activeCategory);
  }, [activeCategory, products]);

  const handleAddProduct = useCallback((product) => {
    setSelectedProducts((prevProducts) => {
      if (prevProducts.length >= MAX_PRODUCTS) return prevProducts;

      const productWithLocalizedNames = {
        id: product.id,
        title: product.title,
        englishTitle: product.englishTitle,
      };

      return [...prevProducts, productWithLocalizedNames];
    });
  }, []);

  const handleRemoveProduct = useCallback((product) => {
    setSelectedProducts((prevProducts) =>
      prevProducts.filter((p) => p.id !== product.id),
    );
  }, []);

  const isProductSelected = useCallback(
    (product) => {
      return selectedProducts.some((selectedProduct) => {
        return (
          selectedProduct.title === product.title &&
          selectedProduct.englishTitle === product.englishTitle
        );
      });
    },
    [selectedProducts],
  );

  const handleIconClick = useCallback((productId) => {
    setEditingProductId(productId);
    setIconPickerVisible(true);
  }, []);

  const handleIconSelect = useCallback(
    (icon) => {
      setProductIcons((prevIcons) => ({
        ...prevIcons,
        [editingProductId]: icon,
      }));
      setIconPickerVisible(false);
      setEditingProductId(null);
    },
    [editingProductId],
  );

  const handleSave = async () => {
    try {
      const productsToSave = selectedProducts.map((product) => {
        const baseProduct = {
          englishName: product.englishTitle,
          productLocalName: product.title,
          icon: productIcons[product.id]?.name || product.icon || "Default",
        };

        if (product.isSaved) {
          return {
            id: product.id,
            ...baseProduct,
          };
        }

        return baseProduct;
      });

      if (isEditMode) {
        await updateHomepageProductsService(eventId, productsToSave, token);
      } else {
        await saveHomepageProductsService(eventId, productsToSave, token);
      }

      const successMessageId = isEditMode
        ? "products_updated_successfully"
        : "products_created_successfully";

      addToast({
        type: "success",
        title: intl.formatMessage({ id: "success" }),
        description: intl.formatMessage({ id: successMessageId }),
      });

      onSave(productsToSave);
      handleCancel();
    } catch (error) {
      console.error("Erro ao salvar produtos:", error);
      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "error_saving_products" }),
      });
    }
  };

  const handleCancel = useCallback(() => {
    setSelectedProducts([]);
    setProductIcons({});
    setActiveCategory(DEFAULT_CATEGORY_KEY);
    setCategories([]);
    setIsEditMode(false);
    onCancel();
  }, [onCancel]);

  const isSaveDisabled = selectedProducts.length < MIN_PRODUCTS;

  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [visible]);

  return (
    <>
      <Modal
        modalIsOpen={visible}
        setIsOpen={handleCancel}
        modalWidth="900px"
        dontCloseByClick={false}
        borderRadius="8px"
      >
        <ModalContent>
          <LeftColumn>
            <ModalTitle>
              <FormattedMessage id="products_services_homepage" />
              <CounterTag>
                {selectedProducts.length}/{MAX_PRODUCTS}
              </CounterTag>
            </ModalTitle>

            <SubTitle>
              <FormattedMessage
                id="choose_products_message"
                values={{
                  productCount: (
                    <strong>
                      <FormattedMessage id="choose_products_count" />
                    </strong>
                  ),
                }}
              />
            </SubTitle>

            <CategoryTagsContainer>
              <CategoryTag
                key={DEFAULT_CATEGORY_KEY}
                $active={activeCategory === DEFAULT_CATEGORY_KEY}
                onClick={() => setActiveCategory(DEFAULT_CATEGORY_KEY)}
              >
                {allCategoriesLabel}
              </CategoryTag>
              {categories.map((category) => (
                <CategoryTag
                  key={category}
                  $active={activeCategory === category}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </CategoryTag>
              ))}
            </CategoryTagsContainer>

            <ProductsList>
              {filteredProducts.length === 0 ? (
                <EmptyProductsList>
                  <EmptyIcon>
                    <RiEmotionSadLine />
                  </EmptyIcon>
                  <EmptyText>
                    <FormattedMessage id="no_products_registered_for_use" />
                  </EmptyText>
                </EmptyProductsList>
              ) : (
                filteredProducts.map((product) => (
                  <ProductItem key={product.id}>
                    <ProductInfo>
                      <ProductName>{product.name}</ProductName>
                      <ProductDescription>
                        {product.description}
                      </ProductDescription>
                    </ProductInfo>
                    <ProductActions>
                      <AddButton
                        type="button"
                        disabled={
                          isProductSelected(product) ||
                          selectedProducts.length >= MAX_PRODUCTS
                        }
                        onClick={() => handleAddProduct(product)}
                      >
                        <FormattedMessage id="add" />
                      </AddButton>
                    </ProductActions>
                  </ProductItem>
                ))
              )}
            </ProductsList>
          </LeftColumn>

          <RightColumn>
            {selectedProducts.length === 0 ? (
              <EmptyState>
                <EmptyIcon>
                  <RiShoppingCart2Fill />
                </EmptyIcon>
                <EmptyText>
                  <FormattedMessage
                    id="choose_products_message"
                    values={{
                      productCount: (
                        <strong>
                          <FormattedMessage id="choose_products_count" />
                        </strong>
                      ),
                    }}
                  />
                </EmptyText>
              </EmptyState>
            ) : (
              <>
                <ModalTitle>
                  <FormattedMessage id="added_products" />
                </ModalTitle>
                <SelectedProductsList>
                  {selectedProducts.map((product) => {
                    const icon = productIcons[product.id];
                    const IconComponent = icon?.component || RiApps2Line;
                    const displayName = isPt
                      ? product.title
                      : product.englishTitle;

                    return (
                      <SelectedProductItem key={product.id}>
                        <ProductLeftSide>
                          <ProductIcon
                            onClick={() => handleIconClick(product.id)}
                          >
                            <IconComponent />
                          </ProductIcon>
                          <ProductName>{displayName}</ProductName>
                        </ProductLeftSide>
                        <ProductRightSide>
                          <DeleteIcon
                            onClick={() => handleRemoveProduct(product)}
                          >
                            <DeleteOutlined />
                          </DeleteIcon>
                        </ProductRightSide>
                      </SelectedProductItem>
                    );
                  })}
                </SelectedProductsList>
              </>
            )}
          </RightColumn>
        </ModalContent>

        <Footer>
          <CancelButton onClick={handleCancel}>
            {intl.formatMessage({ id: "cancel" })}
          </CancelButton>
          <SaveButton disabled={isSaveDisabled} onClick={handleSave}>
            {intl.formatMessage({ id: "save" })}
          </SaveButton>
        </Footer>
      </Modal>

      <IconPickerModal
        visible={iconPickerVisible}
        onCancel={() => {
          setIconPickerVisible(false);
          setEditingProductId(null);
        }}
        onSelect={handleIconSelect}
      />
    </>
  );
};

ProductsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  eventEditionCode: PropTypes.string.isRequired,
  eventId: PropTypes.number.isRequired,
};

export default ProductsModal;
